// Content sections below the cabinet, behind a disclosure toggle.
//
// The cabinet is the signature element and owns the first screen. Everything
// here sits collapsed behind a single small chevron.
//
// The content stays mounted when collapsed - height is animated to zero
// rather than the tree being unmounted - so it is present in the DOM for
// crawlers and reachable by a screen reader through the disclosure button.
// Unmounting it would take it out of the page entirely.
//
// DRAFT COPY. Clause 8.1 allows content drafted by CodeBooth subject to the
// Client's approval - every service line below is a reasonable guess at what
// Brooke does and must be confirmed before launch. Anything marked TODO is a
// fact I could not derive.
import React, { useRef, useState } from "react";
import { motion, useInView } from "motion/react";

const C = {
  red: "#E7001A",
  steel: "#8FA3B0",
  steelLt: "#D7E1E8",
  frost: "#EEF6FA",
  led: "#8FE3F5",
  mute: "#6D8494",
};

const mono = "'JetBrains Mono', ui-monospace, monospace";
const arch = "'Archivo', system-ui, sans-serif";

// ---------------------------------------------------------------------------
// Copy. Kept as data so a change is one edit rather than a hunt through JSX.
// ---------------------------------------------------------------------------
const INTRO = {
  eyebrow: "Since 2000",
  heading: "Twenty-five years of commercial refrigeration",
  body: [
    "Brooke Refrigeration has kept commercial refrigeration running in South Africa since 2000, when Teddy and Ellie started the business as a two-person operation. Today the company serves more than 850 clients across restaurants, retail, food processing and cold chain logistics.",
    "The work splits into three divisions. Maintenance keeps existing plant running. Cold Storage designs and installs new capacity. Requip sells refurbished equipment for operations that need a unit working this week rather than next quarter.",
  ],
};

const SECTIONS = [
  {
    id: "maintenance",
    n: "01",
    eyebrow: "Division 01",
    heading: "Refrigeration maintenance and callouts",
    body: [
      "Refrigeration fails at the worst possible time, and a warm cold room is a stock write-off measured in hours. Brooke runs scheduled maintenance contracts that catch the common failures before they happen, and takes emergency callouts when they happen anyway.",
      "Every job is logged against the equipment it was performed on, so the service history of a unit travels with it rather than living in a technician's memory.",
    ],
    // TODO: confirm this list with Brooke. Drafted from what a commercial
    // refrigeration contractor typically offers.
    services: [
      "Scheduled maintenance contracts",
      "Emergency breakdown callouts",
      "Compressor and condenser servicing",
      "Gas leak detection and recharge",
      "Temperature monitoring and compliance records",
      "Preventative plant inspections",
    ],
  },
  {
    id: "cold-storage",
    n: "02",
    eyebrow: "Division 02",
    heading: "Cold room design and installation",
    body: [
      "Cold storage is a design problem before it is an installation problem. Panel specification, plant sizing, door placement and floor build all determine what the room costs to run for the next fifteen years.",
      "Brooke designs and installs cold rooms, freezer rooms and the refrigeration plant that serves them, for sites ranging from a single restaurant walk-in to multi-chamber facilities.",
    ],
    // TODO: confirm with Brooke.
    services: [
      "Cold room and freezer room design",
      "Insulated panel supply and installation",
      "Refrigeration plant sizing and fitting",
      "Blast freezer installation",
      "Cold chain facility upgrades",
      "Door, seal and floor work",
    ],
  },
  {
    id: "requip",
    n: "03",
    eyebrow: "Division 03",
    heading: "Refurbished commercial refrigeration equipment",
    body: [
      "New commercial refrigeration carries long lead times and prices that do not suit every operation. Requip is Brooke's refurbished stock: units stripped, repaired, tested and made ready to run.",
      "Stock changes constantly. Everything listed has been through the workshop and is sold with the work that was done to it recorded.",
    ],
    // TODO: confirm with Brooke.
    services: [
      "Display fridges and freezers",
      "Under-counter and preparation units",
      "Blast chillers",
      "Ice machines",
      "Condensing units",
      "Cold room panels",
    ],
  },
];

// TODO: confirm coverage with Brooke. Gauteng is an assumption.
const AREAS = {
  eyebrow: "Coverage",
  heading: "Where Brooke works",
  body: "Brooke services commercial refrigeration across Gauteng, with project work taken further afield by arrangement.",
  list: [
    "Pretoria",
    "Johannesburg",
    "Centurion",
    "Midrand",
    "Ekurhuleni",
    "West Rand",
  ],
};

// FAQs earn long-tail traffic and feed the FAQPage schema in index.html.
// Keep the two in step - a mismatch is a structured data error.
const FAQ = {
  eyebrow: "Common questions",
  heading: "Before you call",
  items: [
    {
      q: "How quickly can someone get to a breakdown?",
      a: "Response times depend on the site and whether a maintenance contract is in place. Contract clients are prioritised. Call the office and the job is logged against your site immediately.",
    },
    {
      q: "Do you work on equipment you did not install?",
      a: "Yes. Most maintenance work is on existing plant installed by someone else. The first visit usually includes recording what is on site so future jobs have a starting point.",
    },
    {
      q: "What does a maintenance contract cover?",
      a: "Scheduled visits at an agreed interval, priority on callouts, and a record of every job performed on each unit. The visit frequency depends on how hard the plant is worked.",
    },
    {
      q: "Is refurbished equipment reliable?",
      a: "Refurbished units are stripped, repaired and tested before they are listed. The work performed on each unit is recorded and supplied with it.",
    },
    {
      q: "Can you help specify a cold room before we commit?",
      a: "Yes. Sizing, panel specification and plant selection are part of the design work, and getting them right is what determines the running cost of the room.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Fades a block in the first time it reaches the viewport. once:true so a
// section does not re-animate every time it scrolls past.
// ---------------------------------------------------------------------------
function Reveal({ children, delay = 0, active }) {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={seen && active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
export default function Content() {
  const [open, setOpen] = useState(false);

  return (
    <div style={S.outer}>
      {/* The only affordance. Deliberately quiet - a hairline rule with a
          chevron sitting on it, no label. */}
      <div style={S.toggleRow}>
        <span style={S.rule} />
        <button
          type="button"
          className="bk-disc"
          style={S.toggle}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="bk-content"
          aria-label={open ? "Hide company information" : "Show company information"}
        >
          <motion.span
            style={S.chev}
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden="true">
              <path
                d="M1 1L7 7L13 1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.span>
        </button>
        <span style={S.rule} />
      </div>

      {/* Stays mounted when collapsed. height:auto animates cleanly in motion
          and leaves the markup in the document either way. */}
      <motion.div
        id="bk-content"
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{
          height: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
          opacity: { duration: open ? 0.4 : 0.2, delay: open ? 0.1 : 0 },
        }}
        style={S.collapse}
      >
        <div style={S.wrap}>
          {/* ---------------- intro ---------------- */}
          <section style={S.section}>
            <Reveal active={open}>
              <p style={S.eyebrow}>{INTRO.eyebrow}</p>
              <h1 style={S.h2}>{INTRO.heading}</h1>
              <div style={S.prose}>
                {INTRO.body.map((p, i) => (
                  <p key={i} style={S.p}>{p}</p>
                ))}
              </div>
            </Reveal>
          </section>

          {/* ---------------- divisions ---------------- */}
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} style={S.section}>
              <Reveal active={open}>
                <div style={S.divHead}>
                  <span style={S.divNum}>{s.n}</span>
                  <div>
                    <p style={S.eyebrow}>{s.eyebrow}</p>
                    <h2 style={S.h2}>{s.heading}</h2>
                  </div>
                </div>

                <div style={S.cols}>
                  <div style={S.prose}>
                    {s.body.map((p, i) => (
                      <p key={i} style={S.p}>{p}</p>
                    ))}
                  </div>

                  {/* Set out like a nameplate rather than a bullet list,
                      because that is how this equipment is actually labelled. */}
                  <ul style={S.spec}>
                    {s.services.map((item) => (
                      <li key={item} style={S.specRow}>
                        <span style={S.specDash} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </section>
          ))}

          {/* ---------------- coverage ---------------- */}
          <section style={S.section}>
            <Reveal active={open}>
              <p style={S.eyebrow}>{AREAS.eyebrow}</p>
              <h2 style={S.h2}>{AREAS.heading}</h2>
              <div style={S.prose}>
                <p style={S.p}>{AREAS.body}</p>
              </div>
              <ul style={S.areaList}>
                {AREAS.list.map((a) => (
                  <li key={a} style={S.areaItem}>{a}</li>
                ))}
              </ul>
            </Reveal>
          </section>

          {/* ---------------- faq ---------------- */}
          <section style={S.sectionLast}>
            <Reveal active={open}>
              <p style={S.eyebrow}>{FAQ.eyebrow}</p>
              <h2 style={S.h2}>{FAQ.heading}</h2>
            </Reveal>

            <div style={S.faqList}>
              {FAQ.items.map((f, i) => (
                <Reveal key={f.q} delay={i * 0.04} active={open}>
                  <div style={S.faqItem}>
                    <h3 style={S.faqQ}>{f.q}</h3>
                    <p style={S.faqA}>{f.a}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
const S = {
  outer: { position: "relative", zIndex: 5 },

  // --- disclosure ----------------------------------------------------------
  toggleRow: {
    display: "flex", alignItems: "center",
    maxWidth: 980, margin: "0 auto",
    padding: "0 clamp(20px,5vw,44px)",
  },
  rule: {
    flex: 1, height: 1,
    background: "rgba(143,163,176,.14)",
  },
  toggle: {
    // 44px tall hit area around a 14px mark, so it stays tappable while
    // reading as almost nothing
    flex: "0 0 auto",
    width: 44, height: 44,
    display: "grid", placeItems: "center",
    background: "transparent", border: "none",
    color: C.mute, padding: 0,
  },
  chev: { display: "grid", placeItems: "center", lineHeight: 0 },

  collapse: { overflow: "hidden" },

  wrap: {
    maxWidth: 980,
    margin: "0 auto",
    padding: "clamp(30px,6vh,64px) clamp(20px,5vw,44px) 0",
  },

  section: {
    paddingBottom: "clamp(44px,8vh,88px)",
    borderBottom: "1px solid rgba(143,163,176,.14)",
    marginBottom: "clamp(44px,8vh,88px)",
  },
  sectionLast: { paddingBottom: "clamp(30px,6vh,60px)" },

  eyebrow: {
    fontFamily: mono, fontSize: 10, letterSpacing: ".22em",
    textTransform: "uppercase", color: C.red, marginBottom: 14,
  },
  h2: {
    fontFamily: arch, fontWeight: 800,
    fontSize: "clamp(24px,4.4vw,40px)", lineHeight: 1.08,
    letterSpacing: "-.022em", color: "#fff",
    maxWidth: "20ch", marginBottom: "clamp(20px,3vw,30px)",
  },

  prose: { maxWidth: "62ch" },
  p: {
    fontSize: "clamp(14px,1.9vw,16px)", lineHeight: 1.68,
    color: C.steelLt, marginBottom: 16,
  },

  // --- division heading ----------------------------------------------------
  divHead: {
    display: "flex", alignItems: "flex-start",
    gap: "clamp(14px,3vw,28px)",
    marginBottom: "clamp(20px,3vw,30px)",
  },
  divNum: {
    flex: "0 0 auto",
    fontFamily: mono, fontWeight: 700,
    fontSize: "clamp(11px,1.8vw,13px)", letterSpacing: ".1em",
    color: C.steel, paddingTop: 4,
    borderLeft: `2px solid ${C.red}`, paddingLeft: 12,
  },

  cols: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "clamp(24px,4vw,52px)",
    alignItems: "start",
  },

  // --- spec list -----------------------------------------------------------
  spec: {
    listStyle: "none", margin: 0, padding: 0,
    borderTop: "1px solid rgba(143,163,176,.18)",
  },
  specRow: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "11px 0",
    borderBottom: "1px solid rgba(143,163,176,.12)",
    fontFamily: mono, fontSize: "clamp(10.5px,1.5vw,12px)",
    letterSpacing: ".04em", color: C.steel, lineHeight: 1.45,
  },
  specDash: {
    flex: "0 0 auto", width: 14, height: 1,
    background: C.led, opacity: 0.55,
  },

  // --- coverage ------------------------------------------------------------
  areaList: {
    listStyle: "none", margin: "clamp(20px,3vw,28px) 0 0", padding: 0,
    display: "flex", flexWrap: "wrap", gap: 8,
  },
  areaItem: {
    fontFamily: mono, fontSize: 11, letterSpacing: ".1em",
    textTransform: "uppercase", color: C.steelLt,
    border: "1px solid rgba(143,163,176,.28)",
    borderRadius: 999, padding: "8px 15px",
  },

  // --- faq -----------------------------------------------------------------
  faqList: { display: "grid", gap: 2 },
  faqItem: {
    padding: "clamp(18px,2.6vw,24px) 0",
    borderTop: "1px solid rgba(143,163,176,.14)",
  },
  faqQ: {
    fontFamily: arch, fontWeight: 600,
    fontSize: "clamp(15px,2.2vw,19px)", lineHeight: 1.3,
    color: "#fff", marginBottom: 10,
  },
  faqA: {
    fontSize: "clamp(13.5px,1.8vw,15px)", lineHeight: 1.65,
    color: C.mute, maxWidth: "68ch",
  },
};